import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import apiClient from '../../API/apiClient';
import endpoints from '../../API/endpoints';
import {
  EngagementComment,
  EngagementModelType,
  EngagementSummary,
  EngagementThreadPage,
  ReactionsSummary,
} from './constants';

const E = endpoints.ENGAGEMENT;
const data = (res: any) => res?.data?.data;

export const engagementThreadKey = (modelType: number, modelId: string | number) => [
  'engagement-thread',
  modelType,
  String(modelId),
];
export const engagementSummaryKey = (modelType: number, modelIds: Array<string | number>) => [
  'engagement-summary',
  modelType,
  modelIds.map(String).join(','),
];

// ---------------------------------------------------------------- requests

export const fetchEngagementThread = async (
  modelType: number,
  modelId: string | number,
  before?: number
): Promise<EngagementThreadPage> =>
  data(
    await apiClient.get(`${E.BASE}/${modelType}/${encodeURIComponent(String(modelId))}`, {
      params: before ? { before } : undefined,
    })
  );

export const fetchEngagementReplies = async (
  commentId: number,
  after?: number
): Promise<{ replies: EngagementComment[]; next_after: number | null }> =>
  data(await apiClient.get(`${E.COMMENTS}/${commentId}/replies`, { params: after ? { after } : undefined }));

export const fetchEngagementSummary = async (
  modelType: number,
  modelIds: Array<string | number>
): Promise<EngagementSummary> =>
  data(
    await apiClient.get(E.SUMMARY, {
      params: { modelType, modelIds: modelIds.map(String).join(',') },
      suppressErrorToast: true,
    } as any)
  );

export const setReactionApi = async (
  modelType: number,
  modelId: string | number,
  emoji: string | null
): Promise<{ reactions: ReactionsSummary }> =>
  data(await apiClient.put(`${E.BASE}/${modelType}/${encodeURIComponent(String(modelId))}/reaction`, { emoji }));

export const addCommentApi = async (
  modelType: number,
  modelId: string | number,
  body: string,
  parentId?: number
): Promise<{ comment: EngagementComment }> =>
  data(
    await apiClient.post(`${E.BASE}/${modelType}/${encodeURIComponent(String(modelId))}/comments`, {
      body,
      ...(parentId ? { parent_id: parentId } : {}),
    })
  );

export const editCommentApi = async (id: number, body: string) =>
  data(await apiClient.patch(`${E.COMMENTS}/${id}`, { body }));

export const deleteCommentApi = async (id: number) => data(await apiClient.delete(`${E.COMMENTS}/${id}`));

export const blockUserApi = async (userId: number, reason?: string, hideComments?: boolean) =>
  data(await apiClient.post(E.BLOCKS, { user_id: userId, reason, hide_comments: !!hideComments }));

// ---------------------------------------------------------------- helpers

/** the reactions after toggling `emoji` (WhatsApp style: one per user, same again removes it) */
export const toggleReaction = (summary: ReactionsSummary, emoji: string | null): ReactionsSummary => {
  const counts = { ...summary.counts };
  const next = emoji && emoji !== summary.mine ? emoji : null;
  if (summary.mine) {
    counts[summary.mine] = Math.max((counts[summary.mine] || 1) - 1, 0);
    if (!counts[summary.mine]) delete counts[summary.mine];
  }
  if (next) {
    counts[next] = (counts[next] || 0) + 1;
  }
  return { counts, mine: next };
};

/** patch the reactions of the entity (modelType) or of one comment inside a cached thread */
const patchThreadReactions = (
  pages: EngagementThreadPage[],
  target: { modelType: number; modelId: string },
  update: (summary: ReactionsSummary) => ReactionsSummary
): EngagementThreadPage[] => {
  if (target.modelType !== EngagementModelType.COMMENT) {
    return pages.map((page, index) => (index === 0 ? { ...page, reactions: update(page.reactions) } : page));
  }
  const patchComment = (comment: EngagementComment): EngagementComment => ({
    ...comment,
    reactions: String(comment.id) === target.modelId ? update(comment.reactions) : comment.reactions,
    replies: comment.replies?.map(patchComment),
  });
  return pages.map((page) => ({ ...page, comments: page.comments.map(patchComment) }));
};

// ---------------------------------------------------------------- hooks

/** the thread of an entity - pages of top-level comments (newest first) */
export const useEngagementThread = (modelType: number, modelId: string | number, enabled = true) =>
  useInfiniteQuery(
    engagementThreadKey(modelType, modelId),
    ({ pageParam }) => fetchEngagementThread(modelType, modelId, pageParam),
    {
      enabled: enabled && modelId !== undefined && modelId !== null && modelId !== '',
      getNextPageParam: (last: EngagementThreadPage) => last?.next_before ?? undefined,
      staleTime: 30 * 1000,
      refetchOnWindowFocus: false,
    }
  );

/** reaction / comment counts of many items at once (lists) */
export const useEngagementSummary = (
  modelType: number,
  modelIds: Array<string | number>,
  enabled = true
) =>
  useQuery(engagementSummaryKey(modelType, modelIds), () => fetchEngagementSummary(modelType, modelIds), {
    enabled: enabled && modelIds.length > 0,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
  });

/**
 * Set / toggle a reaction with an optimistic update of the entity's thread (entity reactions or a comment's).
 * `threadKey` = the thread the target is shown in.
 */
export const useSetReaction = (threadKey: unknown[]) => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ modelType, modelId, emoji }: { modelType: number; modelId: string; emoji: string | null }) =>
      setReactionApi(modelType, modelId, emoji),
    {
      onMutate: async ({ modelType, modelId, emoji }) => {
        await queryClient.cancelQueries(threadKey);
        const previous = queryClient.getQueryData<any>(threadKey);
        if (previous?.pages) {
          queryClient.setQueryData(threadKey, {
            ...previous,
            pages: patchThreadReactions(previous.pages, { modelType, modelId }, (s) => toggleReaction(s, emoji)),
          });
        }
        return { previous };
      },
      onError: (_error, _vars, context: any) => {
        if (context?.previous) {
          queryClient.setQueryData(threadKey, context.previous);
        }
      },
      onSuccess: (result, { modelType, modelId }) => {
        // the server's counts win (other users may have reacted meanwhile)
        const current = queryClient.getQueryData<any>(threadKey);
        if (current?.pages && result?.reactions) {
          queryClient.setQueryData(threadKey, {
            ...current,
            pages: patchThreadReactions(current.pages, { modelType, modelId }, () => result.reactions),
          });
        }
        if (modelType !== EngagementModelType.COMMENT) {
          // list counts of this entity type (e.g. the video notifications panel)
          queryClient.invalidateQueries(['engagement-summary', modelType]);
        }
      },
    }
  );
};
